/**
 * Typed contract for GET /api/v1/public/events/:id/viewer.
 *
 * Source of truth: PRD §9.2 / §16.2 and
 * `projects/comrades/fixtures/viewer_payload.json`.
 *
 * This loader reads the committed mock payload by default. When Stream A
 * merges the real endpoint it becomes a one-line swap:
 *
 *     fetch(`/api/v1/public/events/${eventId}/viewer`)
 *
 * instead of the bundled import below. No other call site changes.
 */

import { apiUrl } from '@/lib/apiBase'
import mockPayload from './viewer_payload.fixture.json'
import type { StreamTelemetryPoint } from '@/hooks/useStreamTelemetry'
import type { WorldLayerPayload } from './worldLayer'

export type ViewerCameraStatus = 'active' | 'idle' | 'error' | 'offline' | string

export interface ViewerCameraLocation {
  latitude: number
  longitude: number
  distance_km?: number | null
  label?: string | null
  recorded_at?: string | null
}

export interface ViewerSponsor {
  campaign_id: string
  sponsor_name: string
  click_through_url: string
  logo_url: string | null
  banner_mobile_url: string | null
  banner_pillar_url: string | null
  alt_text: string
}

export interface ViewerCamera {
  id?: string
  participant_identity: string
  /** Optional mirror of participant_identity. Build `WatchCamera.identity` from `participant_identity`. */
  identity?: string
  stream_id: string
  /** A programme is the Live show built from the cameras; it has no location. */
  source_role?: 'camera' | 'programme'
  name: string
  subtitle?: string | null
  status: ViewerCameraStatus
  ad_tag?: string | null
  publicly_visible?: boolean | null
  public_audio_enabled?: boolean | null
  location: ViewerCameraLocation | null
  preview_image_url?: string | null
  sponsor?: ViewerSponsor | null
}

export interface ViewerOrganization {
  id: string
  name: string
  display_name: string | null
  description: string | null
  logo_url: string | null
  verified: boolean
  events_count?: number
}

export interface ViewerEvent {
  id: string
  name: string
  shortcode?: string
  cover_url?: string | null
  /** Retired: every event's page is the watch screen now. Still read from the API, and no longer decides anything. */
  watch_screen_enabled?: boolean
  logo_url?: string | null
  /** The page's header shows `logo_url` top left in place of StreamBridge's: the event asks for it and has a logo (the
   *  server's rule, so it is never true without one). Missing from an older API. */
  logo_in_header?: boolean
  subtitle?: string | null
  description?: string | null
  starts_at?: string | null
  ends_at?: string | null
  /** The event's IANA time zone; the pre-show pins an event with no course on its city. Missing from an older API. */
  timezone?: string | null
  /** The event shows its pre-show before the start (docs/features/pre-show-page.md). Missing from an older API: off. */
  pre_show?: boolean
  live_only?: boolean
  status: string
  /** Template registry key — drives which shell renders the public viewer. */
  template_key?: string | null
  /** Forked layout JSON. Null = render the template's default layout. */
  layout?: Record<string, unknown> | null
  livekit_room_name: string
  livekit_url: string
  viewer_token_endpoint: string
  organization?: ViewerOrganization | null
}

export interface ViewerRouteBounds {
  north: number
  south: number
  east: number
  west: number
}

export interface ViewerRoute {
  id: string
  /** The course's name ("The Cobra Full 42 km"): the map's legend names each course of an event with several by it. */
  name?: string | null
  geojson_url?: string | null
  gpx_url?: string | null
  bounds?: ViewerRouteBounds | null
  /** The parsed GPX's length (`EventRoute#viewer_payload`); the watch screen's bar shows the primary route's. */
  distance_km?: number | null
  /** The course's play world, built on the server: `building`, `ready` or `failed`; null before a build. */
  world_status?: 'building' | 'ready' | 'failed' | null
  /** The built world's pack, its build in the URL so it caches forever; null until it's ready. */
  world_url?: string | null
}

export interface ViewerPayload {
  event: ViewerEvent
  featured_participant_identity: string | null
  cameras: ViewerCamera[]
  route: ViewerRoute | null
  /** Every route of the event; `route` is the primary one. */
  routes?: ViewerRoute[]
  destination?: ViewerDestinationSnapshot
  /**
   * The play world layer (`lib/comrades/worldLayer.ts`): sponsor placements, course points and hotspots, as public
   * elements. Raw from the API: read it with `parseWorldLayer` (`useWorldLayer` does). Absent from an older API.
   */
  world?: WorldLayerPayload | null
  updated_at: string
}

export interface ViewerDestinationComponentLiveState {
  state: Record<string, unknown>
  revision: number
  updated_at: string | null
  expires_at: string | null
}

export interface ViewerDestinationSnapshot {
  event_id: string
  mode: 'viewer' | 'preview' | 'studio'
  layout: Record<string, unknown>
  layout_revision: number
  live_state: {
    revision: number
    components: Record<string, ViewerDestinationComponentLiveState>
  }
  data_sources: Array<{
    id: string
    event_id: string
    source_key: string
    capabilities: Array<'tracking.v1' | 'results.v1'>
    adapter_key: string
    adapter_version: string
    public_config: Record<string, unknown>
    status: string
    last_fresh_at: string | null
  }>
}

export type ViewerDestinationUpdateResult = {
  payload: ViewerPayload
  status: 'applied' | 'ignored' | 'gap'
}

export interface ViewerPayloadResponse {
  data: ViewerPayload
}

export interface ViewerStreamPreview {
  id?: string | number | null
  stream_id?: string | number | null
  name?: string | null
  status?: ViewerCameraStatus | null
  subtitle?: string | null
  ad_tag?: string | null
  identity?: string | null
  participant_identity?: string | null
  participantIdentity?: string | null
  publicly_visible?: boolean | null
  public_audio_enabled?: boolean | null
  location?: ViewerCameraLocation | null
  preview_image_url?: string | null
  sponsor?: ViewerSponsor | null
}

export interface ViewerPayloadMergeResult {
  payload: ViewerPayload
  known: boolean
}

/** A viewer payload request the API answered with an error; `status` 404 means there is no such event. */
export class ViewerPayloadError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ViewerPayloadError'
    this.status = status
  }
}

/**
 * Loads the viewer payload for a given event. Calls the real
 * `GET /api/v1/public/events/:id/viewer` endpoint (Stream A / PR #131).
 *
 * If `VITE_VIEWER_USE_MOCK=1` is set at build time, the bundled fixture is
 * returned instead — handy for Storybook-style offline demos.
 */
export async function loadViewerPayload(eventId: string): Promise<ViewerPayload> {
  if (import.meta.env.VITE_VIEWER_USE_MOCK === '1') {
    void eventId
    return (mockPayload as unknown as ViewerPayloadResponse).data
  }

  const url = apiUrl(`public/events/${encodeURIComponent(eventId)}/viewer`)
  const res = await fetch(url, {
    credentials: 'omit',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new ViewerPayloadError(res.status, `Failed to load viewer payload (${res.status} ${res.statusText})`)
  }
  const body = (await res.json()) as ViewerPayloadResponse
  return body.data
}

/** True when this camera has valid coordinates and should render as a map pin. */
export function isMappable(camera: ViewerCamera): camera is ViewerCamera & {
  location: ViewerCameraLocation
} {
  const loc = camera.location
  return Boolean(
    loc &&
      Number.isFinite(loc.latitude) &&
      Number.isFinite(loc.longitude) &&
      Math.abs(loc.latitude) <= 90 &&
      Math.abs(loc.longitude) <= 180,
  )
}

export function visibleViewerCameras(
  payload: ViewerPayload,
  livePublisherIdentities: ReadonlySet<string>,
): ViewerCamera[] {
  if (payload.event.live_only !== true) return payload.cameras

  return payload.cameras.filter((camera) =>
    livePublisherIdentities.has(camera.participant_identity),
  )
}

export function visibleViewerPayload(
  payload: ViewerPayload,
  livePublisherIdentities: ReadonlySet<string>,
): ViewerPayload {
  const cameras = visibleViewerCameras(payload, livePublisherIdentities)
  const featuredIdentity = payload.featured_participant_identity
  const featuredVisible =
    featuredIdentity !== null &&
    cameras.some((camera) => camera.participant_identity === featuredIdentity)
  const nextFeaturedIdentity = featuredVisible ? featuredIdentity : null

  if (
    cameras === payload.cameras &&
    nextFeaturedIdentity === payload.featured_participant_identity
  ) {
    return payload
  }

  return {
    ...payload,
    featured_participant_identity: nextFeaturedIdentity,
    cameras,
  }
}

/**
 * Overlay fresh LiveKit data-channel telemetry on the persisted viewer payload.
 * The hook removes stale points, so a missing point naturally falls back to the
 * API's last-known location without another network request.
 */
export function mergeViewerCameraTelemetry(
  cameras: ViewerCamera[],
  telemetryPoints: StreamTelemetryPoint[],
): ViewerCamera[] {
  if (telemetryPoints.length === 0) return cameras

  const telemetryByStream = new Map(
    telemetryPoints.map((point) => [String(point.streamId), point]),
  )
  let changed = false
  const nextCameras = cameras.map((camera) => {
    const point = telemetryByStream.get(String(camera.stream_id ?? camera.id))
    if (!point) return camera

    const location: ViewerCameraLocation = {
      ...camera.location,
      latitude: point.latitude,
      longitude: point.longitude,
      recorded_at: point.timestamp,
    }
    if (
      camera.location?.latitude === location.latitude &&
      camera.location?.longitude === location.longitude &&
      camera.location?.recorded_at === location.recorded_at
    ) {
      return camera
    }

    changed = true
    return { ...camera, location }
  })

  return changed ? nextCameras : cameras
}

export function updateViewerEventSettings(
  payload: ViewerPayload,
  eventSettings: Partial<Pick<ViewerEvent, 'live_only'>>,
): ViewerPayload {
  if (
    eventSettings.live_only === undefined ||
    eventSettings.live_only === payload.event.live_only
  ) {
    return payload
  }

  return {
    ...payload,
    event: {
      ...payload.event,
      live_only: eventSettings.live_only,
    },
    updated_at: new Date().toISOString(),
  }
}

export function updateViewerDestinationLayout(
  payload: ViewerPayload,
  update: {
    layout_revision: number
    layout?: Record<string, unknown>
    live_state_revision?: number
  },
): ViewerDestinationUpdateResult {
  const destination = payload.destination
  if (!destination) {
    return { payload, status: 'gap' }
  }
  if (update.layout_revision <= destination.layout_revision) {
    return { payload, status: 'ignored' }
  }
  if (update.layout_revision > destination.layout_revision + 1 || !update.layout) {
    return { payload, status: 'gap' }
  }

  return {
    status: 'applied',
    payload: {
      ...payload,
      destination: {
        ...destination,
        layout: update.layout,
        layout_revision: update.layout_revision,
        live_state: {
          ...destination.live_state,
          revision: Math.max(
            destination.live_state.revision,
            update.live_state_revision ?? destination.live_state.revision,
          ),
        },
      },
      updated_at: new Date().toISOString(),
    },
  }
}

export function updateViewerDestinationLiveState(
  payload: ViewerPayload,
  update: {
    revision: number
    component_instance_id: string
    state: Record<string, unknown> | null
    updated_at: string | null
    expires_at: string | null
  },
): ViewerDestinationUpdateResult {
  const destination = payload.destination
  if (!destination || update.revision > destination.live_state.revision + 1) {
    return { payload, status: 'gap' }
  }
  if (update.revision <= destination.live_state.revision) {
    return { payload, status: 'ignored' }
  }

  const components = { ...destination.live_state.components }
  if (update.state === null) {
    delete components[update.component_instance_id]
  } else {
    components[update.component_instance_id] = {
      state: update.state,
      revision: update.revision,
      updated_at: update.updated_at,
      expires_at: update.expires_at,
    }
  }

  return {
    status: 'applied',
    payload: {
      ...payload,
      destination: {
        ...destination,
        live_state: {
          revision: update.revision,
          components,
        },
      },
      updated_at: new Date().toISOString(),
    },
  }
}

export function removeViewerCamera(payload: ViewerPayload, streamId: string): ViewerPayload {
  const removed = payload.cameras.find((camera) => String(camera.stream_id) === String(streamId))
  if (!removed) return payload

  const cameras = payload.cameras.filter((camera) => String(camera.stream_id) !== String(streamId))

  return {
    ...payload,
    cameras,
    featured_participant_identity:
      removed?.participant_identity === payload.featured_participant_identity
        ? null
        : payload.featured_participant_identity,
    updated_at: new Date().toISOString(),
  }
}

export function updateViewerFeaturedCamera(
  payload: ViewerPayload,
  featuredParticipantIdentity: string | null,
): ViewerPayload {
  if (payload.featured_participant_identity === featuredParticipantIdentity) {
    return payload
  }

  return {
    ...payload,
    featured_participant_identity: featuredParticipantIdentity,
    updated_at: new Date().toISOString(),
  }
}

export function mergeViewerStreamPreview(
  payload: ViewerPayload,
  preview: ViewerStreamPreview,
): ViewerPayloadMergeResult {
  const streamId = previewStreamId(preview)
  if (!streamId) return { payload, known: true }

  if (preview.publicly_visible === false) {
    return { payload: removeViewerCamera(payload, streamId), known: true }
  }

  const identity = previewParticipantIdentity(preview)
  const cameraIndex = payload.cameras.findIndex((camera) => (
    String(camera.stream_id) === streamId ||
    (identity !== null && camera.participant_identity === identity)
  ))

  if (cameraIndex === -1) {
    return { payload, known: false }
  }

  const cameras = payload.cameras.slice()
  const camera = cameras[cameraIndex]
  const nextIdentity = identity ?? camera.participant_identity
  const nextName = preview.name ?? camera.name
  const nextStatus = preview.status ?? camera.status
  const nextPubliclyVisible =
    preview.publicly_visible !== undefined
      ? preview.publicly_visible
      : camera.publicly_visible
  const nextCamera: ViewerCamera = {
    ...camera,
    id: streamId,
    stream_id: streamId,
    name: nextName,
    status: nextStatus,
    participant_identity: nextIdentity,
    subtitle: preview.subtitle !== undefined ? preview.subtitle : camera.subtitle,
    ad_tag: preview.ad_tag !== undefined ? preview.ad_tag : camera.ad_tag,
    publicly_visible:
      camera.publicly_visible === undefined && nextPubliclyVisible === true
        ? undefined
        : nextPubliclyVisible,
    public_audio_enabled:
      preview.public_audio_enabled !== undefined && preview.public_audio_enabled !== null
        ? preview.public_audio_enabled
        : camera.public_audio_enabled,
    location: preview.location !== undefined ? preview.location : camera.location,
    preview_image_url:
      preview.preview_image_url !== undefined
        ? preview.preview_image_url
        : camera.preview_image_url,
    sponsor: preview.sponsor !== undefined ? preview.sponsor : camera.sponsor,
  }

  if (viewerCameraEqual(camera, nextCamera)) {
    return { payload, known: true }
  }

  cameras[cameraIndex] = nextCamera

  return {
    payload: {
      ...payload,
      cameras,
      updated_at: new Date().toISOString(),
    },
    known: true,
  }
}

function previewStreamId(preview: ViewerStreamPreview): string | null {
  const id = preview.stream_id ?? preview.id
  return id === null || id === undefined ? null : String(id)
}

function previewParticipantIdentity(preview: ViewerStreamPreview): string | null {
  return preview.participant_identity ?? preview.identity ?? preview.participantIdentity ?? null
}

function viewerCameraEqual(a: ViewerCamera, b: ViewerCamera): boolean {
  return (
    a.id === b.id &&
    a.stream_id === b.stream_id &&
    a.participant_identity === b.participant_identity &&
    a.name === b.name &&
    a.subtitle === b.subtitle &&
    a.status === b.status &&
    a.ad_tag === b.ad_tag &&
    a.publicly_visible === b.publicly_visible &&
    a.public_audio_enabled === b.public_audio_enabled &&
    a.preview_image_url === b.preview_image_url &&
    JSON.stringify(a.location ?? null) === JSON.stringify(b.location ?? null) &&
    JSON.stringify(a.sponsor ?? null) === JSON.stringify(b.sponsor ?? null)
  )
}
